<template>
  <div id="gatewayNetworkEvents">
    <card :title="$t('g-networkEvents:filter')" :visible="false" collapsible>
      <div slot="card-body">
        <div class="form-row">
          <div class="form-group col-md-6">
            <label for="name" class="control-label">{{ $t('g-networkEvents:name') | capitalize }}</label>
            <select id="name" v-model="filterName" class="form-control">
              <option value="" />
              <option v-for="type in eventsNames" :key="type" :value="type">{{ type }}</option>
            </select>
          </div>

          <div class="form-group col-md-6">
            <label for="type" class="control-label">{{ $t('g-events:type') | capitalize }}</label>
            <select id="type" v-model="filterType" class="form-control" name="type">
              <option value="" />
              <option value="ALARM">{{ $t('g-events:ALARM') | capitalize }}</option>
              <option value="NOTIFICATION">{{ $t('g-events:NOTIFICATION') | capitalize }}</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-6">
            <label for="period" class="control-label">{{ $t('g-networkEvents:period') | capitalize }}</label>
            <flat-pickr id="period"
                        v-model="filterPeriod"
                        :config="fpConfig"
                        name="period"
                        class="form-control"
            />
            <div class="invalid-feedback">{{ errors.first('period') }}</div>
          </div>
        </div>
      </div>

      <div slot="card-footer">
        <button id="filterButton" class="btn btn-primary" @click="filter()">{{ $t('g-networkEvents:search') | capitalize }}</button>
      </div>
    </card>

    <grid id="gatewayNetworkEventsGrid"
          :title="$t('g-networkEvents:events')"
          :columns="columns"
          :paginated-dto="eventsRows"
          @load="filter"
    >
      <template slot="date" slot-scope="props">{{ props.dto.instant | date }}</template>
    </grid>
  </div>
</template>

<script>
import { sortBy, upperFirst, isEmpty, round } from 'lodash-es'
import { mapGetters } from 'vuex'
import to from '@/modules/await-to-js'

const names = ['FRQ_DUTY_CYCLE']

export default {
  titleKey: 'gatewayNetworkEvents',

  props: {
    eui: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      // Filters
      filterName: '',
      filterType: '',
      filterPeriod: '',
      // Data
      eventsRows: {},

      // In data to be modified if needed
      fpConfig: {
        mode: 'range',
        enableTime: true,
        altInput: true,
        altFormat: 'Y/m/d H:i',
        dateFormat: 'U',
        time_24hr: true
      }
    }
  },

  computed: {
    ...mapGetters(['gateway']),

    eventsNames () { return sortBy(names) },

    columns () {
      return [
        { text: this.$t('g-networkEvents:date'), slot: 'date' },
        { text: this.$t('g-networkEvents:type'), formatter: event => upperFirst(this.$t('g-networkEvents:' + event.type)) },
        { text: this.$t('g-networkEvents:name'), formatter: event => event.name },
        { text: this.$t('g-networkEvents:value'), formatter: event => round(event.value, 3) }
      ]
    }
  },

  watch: {
    eui () {
      // Reset data, and get data from new gateway
      this.eventsRows = {}
    },

    gateway () {
      // Do not refresh is we olready have the data
      if (!isEmpty(this.eventsRows)) return

      this.filter()
    }
  },

  created () {
    this.filter()
  },

  methods: {
    async filter (options = {}) {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      if (!isEmpty(this.gateway)) {
        let search = []

        // Add criteria
        if (this.filterName) {
          search = this.$utils.addSearch(search, 'eq', this.filterName, 'name')
        }

        if (this.filterType) {
          search = this.$utils.addSearch(search, 'eq', this.filterType, 'type')
        }

        // Flatpickr return a string "<from> to <to>" with <from> and <to> dates in seconds
        const filterPeriod = this.filterPeriod ? this.filterPeriod.split(' to ').map(v => v * 1000) : []
        if (!isEmpty(filterPeriod)) {
          // Dates must be used as long representing a timestamps in ms
          search = this.$utils.addSearch(search, 'gte', filterPeriod[0], 'instant')
          search = this.$utils.addSearch(search, 'lte', filterPeriod[1], 'instant')
        }

        // Short API urls, add gateway eui in search
        search = this.$utils.addSearch(search, 'eq', this.gateway.eui, 'eui')

        // Format criteria
        if (search.length > 0) {
          options.search = JSON.stringify(search.length === 1 ? search[0] : this.$utils.formatSearch('and', search))
        }

        // Get filtered data
        this.getEvents(options)
      }
    },

    async getEvents (options) {
      const params = this.$utils.getParams({ sort: '-date' }, options, this.queryPageParams)
      let error, response
      [error, response] = await to(this.$api.getRncGatewaysEvents(this.$store.getters.application, params))
      if (error || !response) return

      this.eventsRows = response.data
    }
  }
}
</script>
